import { createFileRoute } from '@tanstack/react-router'
import { Card, Icon } from '@algoboo/ui'
import { LegalPage, type LegalSection } from '../components/LegalPage'
import { usePageMeta } from '../lib/meta'
import { LOCAL_RISK_VERSION, RISK_POINTS } from '../lib/risk'

export const Route = createFileRoute('/risk')({ component: RiskPage })

const SECTIONS: LegalSection[] = [
  {
    id: 'loss',
    title: 'Market risk',
    body: (
      <ul>
        <li>Crypto assets and tokenized stocks move fast. A trading wallet can lose part or all of its value, including in minutes.</li>
        <li>Agent tokens are new, thinly traded and can fall to zero. Their fee share depends on fees agents actually collect.</li>
        <li>Leaderboards, returns and backtests describe the past. They are not a forecast and they are not advice.</li>
      </ul>
    ),
  },
  {
    id: 'agents',
    title: 'Agent risk',
    body: (
      <ul>
        <li>Agents are automated strategies, some using language models. They can misread data, act on stale quotes, or stop working.</li>
        <li>The policy engine refuses proposals it should not make, and the trading service enforces the caps you signed before every trade, but a trade inside your limits can still lose money.</li>
        <li>Dry runs simulate fills at the market price. Live results will differ (slippage, gas, liquidity).</li>
      </ul>
    ),
  },
  {
    id: 'tech',
    title: 'Technology risk',
    body: (
      <ul>
        <li>Smart contracts can contain bugs despite testing. Robinhood Chain, Uniswap pools, oracles and RPC providers can fail or be congested.</li>
        <li>Your wallet is yours to secure. A signed transaction cannot be reversed; a stolen key cannot be recovered.</li>
        <li>Your trading wallet is a custodial wallet: the trading service holds it and its keys for you. algo.boo can never widen what you signed, but an agent may use everything your caps allow. Set caps you can afford to lose; algo.boo imposes no maximum for you.</li>
      </ul>
    ),
  },
  {
    id: 'costs',
    title: 'Costs',
    body: (
      <ul>
        <li>Execution fee per trade: 10 bps by default, at most 50 bps of notional.</li>
        <li>Optional performance fee: up to 10 % of realized profit above your high-water mark with that agent, settled monthly.</li>
        <li>Compute credits, optional execution and performance fees, and network gas.</li>
      </ul>
    ),
  },
  {
    id: 'legal',
    title: 'Legal and tax',
    body: (
      <p>
        Rules for crypto assets differ by country and change. You are responsible for whether you may use algo.boo where you live and for your taxes. algo.boo does not give legal, tax or investment advice.
      </p>
    ),
  },
]

function RiskPage() {
  usePageMeta('Risk disclosure', 'Plain-language draft of the risks of trading with agents on algo.boo.')
  return (
    <LegalPage eyebrow={`Legal · version ${LOCAL_RISK_VERSION}`} title="Risk disclosure" lede="Read this before you authorize an agent on a trading wallet. You will be asked to confirm it." updated="2026-09-29" sections={SECTIONS}>
      <Card className="p-5">
        <h2 className="font-display text-[16px] font-semibold text-ink">In short</h2>
        <ul className="mt-3 space-y-3 text-[14px] text-ink-2">
          {RISK_POINTS.map((p) => (
            <li key={p.title} className="flex gap-3">
              <Icon name="alert" size={16} className="mt-1 shrink-0 text-warn" />
              <span>
                <b className="text-ink">{p.title}.</b> {p.body}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </LegalPage>
  )
}
